import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { createLiveSession } from '../liveSessionApi';
import {
  buildLivePlayerPath,
  buildLiveSessionPath,
  liveMatchOnSession,
  pickLatestOpenSession,
} from '../lib/pickJoinTarget';
import { useIsStrategist } from './useIsStrategist';
import { useOpenLiveSession } from './useOpenLiveSession';

export type StrategistLiveEntryMode = 'hidden' | 'start' | 'join';

export type UseStrategistLiveEntryResult = {
  mode: StrategistLiveEntryMode;
  label: string;
  busy: boolean;
  error: string | null;
  /** Open sessions for the preferred team (open status only, newest first). */
  openSessions: ReturnType<typeof useOpenLiveSession>['sessions'];
  sessionsLoading: boolean;
  refetch: () => void;
  run: () => Promise<void>;
  clearError: () => void;
};

/**
 * Strategist Start / Join live session entry (top bar + lobby).
 * Start → create empty session + open waiting URL.
 * Join → open latest live match, or waiting URL if session has no match yet.
 */
export function useStrategistLiveEntry(teamId: string | undefined): UseStrategistLiveEntryResult {
  const { user } = useAuth();
  const isStrategist = useIsStrategist(teamId);
  const { sessions, joinTarget, loading: sessionsLoading, refetch } = useOpenLiveSession(
    user?.id,
    teamId,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openSessions = useMemo(() => {
    if (!teamId) return [];
    return sessions
      .filter((s) => s.teamId === teamId && (s.status ?? '').toLowerCase() === 'open')
      .slice()
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) return Date.parse(b.createdAt) - Date.parse(a.createdAt);
        return 0;
      });
  }, [sessions, teamId]);

  const mode: StrategistLiveEntryMode = useMemo(() => {
    if (!isStrategist || !teamId) return 'hidden';
    if (openSessions.length > 0) return 'join';
    return 'start';
  }, [isStrategist, teamId, openSessions.length]);

  const label = mode === 'join' ? 'Join Live Session' : 'Start Live Session';

  const run = useCallback(async () => {
    if (!teamId || mode === 'hidden' || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'start') {
        const session = await createLiveSession(teamId);
        const path = buildLiveSessionPath(teamId, session.id);
        window.open(path, '_blank', 'noopener,noreferrer');
        refetch();
        return;
      }

      // join
      if (joinTarget) {
        window.open(buildLivePlayerPath(joinTarget), '_blank', 'noopener,noreferrer');
        return;
      }
      const latest = pickLatestOpenSession(sessions, teamId);
      if (latest) {
        const live = liveMatchOnSession(latest);
        if (live) {
          window.open(
            buildLivePlayerPath({
              teamId: latest.teamId,
              sessionId: latest.id,
              matchId: live.id,
              map: live.map,
            }),
            '_blank',
            'noopener,noreferrer',
          );
        } else {
          window.open(buildLiveSessionPath(latest.teamId, latest.id), '_blank', 'noopener,noreferrer');
        }
        return;
      }
      // Race: sessions emptied — fall back to start
      const session = await createLiveSession(teamId);
      window.open(buildLiveSessionPath(teamId, session.id), '_blank', 'noopener,noreferrer');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [teamId, mode, busy, joinTarget, sessions, refetch]);

  const clearError = useCallback(() => setError(null), []);

  return {
    mode,
    label,
    busy,
    error,
    openSessions,
    sessionsLoading,
    refetch,
    run,
    clearError,
  };
}

/** Open a specific open session in a new window (waiting or player). */
export function openSessionWindow(session: {
  teamId: string;
  id: string;
  matches?: { id: string; map: string; status: string }[];
}): void {
  const live = (session.matches ?? []).find((m) => (m.status ?? '').toLowerCase() === 'live');
  if (live) {
    window.open(
      buildLivePlayerPath({
        teamId: session.teamId,
        sessionId: session.id,
        matchId: live.id,
        map: live.map,
      }),
      '_blank',
      'noopener,noreferrer',
    );
    return;
  }
  window.open(buildLiveSessionPath(session.teamId, session.id), '_blank', 'noopener,noreferrer');
}
